import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSEO } from "../hooks/useSEO";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);
  useSEO("Payment Confirmed | NAKSRA", "Your NAKSRA order is confirmed.");

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.payment_status === "failed" || data.payment_status === "expired") {
          setStatus("failed");
          return;
        }
      } catch {
        // keep polling
      }
      attempts.current += 1;
      if (attempts.current < 30) timer = setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#050507] text-[#F8F9FA]">
      <Navbar />
      <main className="mx-auto max-w-2xl px-6 py-32 text-center" data-testid="payment-success-page">
        {status === "checking" && (
          <div data-testid="payment-status-checking">
            <Loader2 className="mx-auto h-10 w-10 animate-spin text-[#D4AF37]" />
            <h1 className="mt-6 font-heading text-4xl font-medium">Confirming your payment…</h1>
            <p className="mt-4 text-[#A1A1AA]">This takes just a moment.</p>
          </div>
        )}
        {status === "paid" && (
          <div data-testid="payment-status-paid">
            <CheckCircle2 className="mx-auto h-12 w-12 text-[#17B890]" />
            <h1 className="mt-6 font-heading text-4xl font-medium tracking-tight sm:text-5xl">Your order is confirmed</h1>
            <p className="mt-6 leading-relaxed text-[#A1A1AA]">
              Thank you. A confirmation email is on its way to your inbox. A qualified Vedic astrologer will now verify and prepare your report — you'll receive another email the moment it's ready, and it will appear in your account for download.
            </p>
            <Link to="/account" data-testid="success-account-cta" className="mt-10 inline-block rounded-full bg-[#D4AF37] px-8 py-3 text-sm font-semibold text-black transition-colors duration-300 hover:bg-[#FBE18D] active:scale-[0.98]">
              Go to my reports
            </Link>
          </div>
        )}
        {(status === "failed" || status === "timeout") && (
          <div data-testid="payment-status-failed">
            <XCircle className="mx-auto h-12 w-12 text-red-400" />
            <h1 className="mt-6 font-heading text-4xl font-medium">We couldn't confirm the payment</h1>
            <p className="mt-6 leading-relaxed text-[#A1A1AA]">
              {status === "timeout"
                ? "The payment is taking longer than expected to confirm. If you were charged, your order will appear in your account shortly."
                : "The payment was not completed. You have not been charged."}
            </p>
            <div className="mt-10 flex justify-center gap-4">
              <Link to="/account" data-testid="failed-account-cta" className="rounded-full border border-white/20 px-8 py-3 text-sm font-medium text-white transition-colors duration-300 hover:bg-white/5">
                Check my account
              </Link>
              <Link to="/services" data-testid="failed-services-cta" className="rounded-full bg-[#D4AF37] px-8 py-3 text-sm font-semibold text-black transition-colors duration-300 hover:bg-[#FBE18D]">
                Try again
              </Link>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
